import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import useDisplay from "../../hooks/useDisplay";
import PCContainer from "../../components/atoms/layout/pcContainer";
import LSRgDisplay02 from "../../components/molecules/text/linSeedKr/Rg/lsRgDisplay02";
import { customerServiceTabList } from "../../constant/customerServiceTab";
import LSRgH2HeadLine from "./../../components/molecules/text/linSeedKr/Rg/lsRgH2HeadLine";
import Pagination from "../../components/molecules/pagination";
import { route } from "./../../router/route";
import LSRgBody01 from "./../../components/molecules/text/linSeedKr/Rg/lsRgBody01";
import LSRgH1HeadLine from "../../components/molecules/text/linSeedKr/Rg/lsRgH1HeadLine";
import MOContainer from "./../../components/atoms/layout/moContainer";
import SelectBox from "../../components/organisms/SelectBox";
import LSRgCaption01 from "../../components/molecules/text/linSeedKr/Rg/lsRgCaption01";
import NoticeApi from "../../api/notice.api";
import moment from "moment";
import StyledCategoryItem from "../../components/molecules/button/styledCategoryItem";
import Loading from "../../components/templates/loading";
import { toast } from "react-toastify";
import Empty from "../../components/molecules/empty";

const CustomerServiceNotice = () => {
  const navigate = useNavigate();
  const { isMobile, isTablet, isDesktop } = useDisplay();
  const isSmall = isMobile || isTablet;
  const [searchParams, setSearchParams] = useSearchParams(); // eslint-disable-line
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);

  // ---------- 모바일 버전 탭 관련 ---------- //
  const [subMenuBoxOpen, setSubMenuBoxOpen] = useState(false);
  const [selectedSubMenu, setSelectedSubMenu] = useState(
    customerServiceTabList[0].title,
  );

  // ---------- 마운트가 되면 ---------- //
  useEffect(() => {
    getNoticeList();
  }, [location]); // eslint-disable-line

  // ---------- 페이지네이션 관련 ---------- //
  const page = useMemo(() => {
    return searchParams.get("page") ? searchParams.get("page") : 1;
  }, [searchParams]);

  const [pagination, setPagination] = useState({
    size: 5, // 한 페이지에 표시될 게시글 수
    rangeSize: 5, // 하단 페이지 사이즈
    totalCount: 0,
    totalPages: 1,
  });

  // ---------- 게시물 리스트 가져오기 ---------- //
  const [noticeList, setNoticeList] = useState([]);

  const getNoticeList = async () => {
    setIsLoading(true);
    try {
      const data = {
        page: page || 1,
        size: pagination.size,
      };
      const response = (await NoticeApi.getList(data)).data.data;

      const processedData = response.content.map((item) => {
        return {
          ...item,
          isOpen: false,
          content: "",
        };
      });

      setNoticeList(processedData);

      setPagination({
        ...pagination,
        totalCount: response.totalCount,
        totalPages:
          response.totalPage ||
          Math.ceil(response.totalCount / pagination.size),
      });
    } catch (error) {
      console.error(error);
      toast("공지사항을 불러오지 못했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // ---------- 게시물 데이터 가져오기 ---------- //
  const getNoticeDetail = async (id) => {
    const selected = noticeList.find((item) => item.id === id);
    if (!selected) return;

    if (selected.isOpen || selected.content) {
      setNoticeList((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, isOpen: !item.isOpen } : item,
        ),
      );
      return;
    }

    try {
      const response = (await NoticeApi.getDetail(id)).data.data.content;
      setNoticeList((prev) =>
        prev.map((item) =>
          item.id === id
            ? { ...item, isOpen: true, content: response.content }
            : item,
        ),
      );
    } catch (error) {
      console.error(error);
      toast("공지사항 내용을 불러오지 못했습니다.");
    }
  };

  if (isLoading) return <Loading />;

  return (
    <HomeSection $isSmall={isSmall}>
      {isDesktop && (
        <PCContainer
          style={{
            flexDirection: "column",
            width: "100%",
            padding: isSmall ? "0px 20px" : "0px",
          }}
        >
          <LSRgDisplay02
            style={{
              marginBottom: 32,
            }}
          >
            📢 고객센터
          </LSRgDisplay02>
          <div
            style={{
              display: "flex",
              width: "100%",
              height: "fit-content",
              gap: 30,
            }}
          >
            {/* 탭 영역 */}
            <TabContainer>
              {customerServiceTabList.map((tab, index) => (
                <StyledCategoryItem
                  key={index}
                  onClick={() => navigate(tab.path)}
                  isActive={index === 0 ? true : false}
                  title={tab.title}
                />
              ))}
            </TabContainer>
            {/* 본문 영역 */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 30,
              }}
            >
              <ContentContainer>
                <ContentHeaderWrapper>
                  <LSRgDisplay02 style={{ color: "#000" }}>
                    공지사항
                  </LSRgDisplay02>
                </ContentHeaderWrapper>

                {/* 본문 영역 - 테이블 */}
                <Table>
                  <tbody>
                    {noticeList.map((item, index) => (
                      <React.Fragment key={item.id}>
                        <Tr
                          style={{
                            borderTop: index === 0 && "1px solid #dbdde0",
                            cursor: "pointer",
                          }}
                          onClick={() => getNoticeDetail(item.id)}
                        >
                          <td
                            style={{
                              marginRight: 312,
                            }}
                          >
                            <LSRgH2HeadLine
                              style={{
                                color: "#101113",
                                width: 575,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {item?.title || ""}
                            </LSRgH2HeadLine>
                          </td>
                          <td>
                            <LSRgBody01 style={{ color: "#898E99" }}>
                              {moment(item.createdAt).format("YYYY.MM.DD")}
                            </LSRgBody01>
                          </td>
                        </Tr>
                        {item.isOpen && (
                          <tr
                            style={{
                              width: 995,
                              minHeight: 63,
                              height: "fit-content",
                              borderBottom: "1px solid #dbdde0",
                              padding: "40px 10px",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <td>
                              <LSRgH1HeadLine
                                style={{
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                }}
                              >
                                {item?.content || ""}
                              </LSRgH1HeadLine>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>

                {noticeList.length === 0 && (
                  <Empty title={"등록된 공지사항이 없습니다."} />
                )}

                {noticeList.length > 0 && pagination.totalCount > 5 && (
                  <Pagination
                    route={route.customer_service_notice}
                    queryString={``}
                    currentPage={Number(page) || 1}
                    totalPages={pagination.totalPages}
                    rangeSize={pagination.rangeSize}
                  />
                )}
              </ContentContainer>
            </div>
          </div>
        </PCContainer>
      )}

      {/* ******************************** */}
      {/* ********** 모바일 버전 ********** */}
      {/* ******************************** */}
      {isSmall && (
        <MOContainer>
          <ContentContainer $isSmall={isSmall}>
            <ContentHeaderWrapper $isSmall={isSmall}>
              <LSRgH1HeadLine>📢 고객센터</LSRgH1HeadLine>
              <SelectBox
                style={{
                  width: "100%",
                }}
                subMenuStyle={{
                  width: "100%",
                }}
                isSmall={true}
                isOpen={subMenuBoxOpen}
                isLink={true}
                selectedValue={selectedSubMenu}
                wrapperHandler={() => {
                  setSubMenuBoxOpen(!subMenuBoxOpen);
                }}
                itemList={customerServiceTabList}
                selectedValueSetterFunc={setSelectedSubMenu}
              />
            </ContentHeaderWrapper>

            <LSRgH1HeadLine style={{ marginBottom: 20 }}>
              공지사항
            </LSRgH1HeadLine>

            {/* 본문 영역 - 테이블 */}
            <Table $isSmall={isSmall}>
              <tbody>
                {noticeList.map((item, index) => (
                  <React.Fragment key={item.id}>
                    <Tr
                      $isSmall={isSmall}
                      style={{
                        borderTop: index === 0 && "1px solid #dbdde0",
                        cursor: "pointer",
                      }}
                      onClick={() => getNoticeDetail(item.id)}
                    >
                      <td
                        style={{
                          marginRight: 84,
                        }}
                      >
                        <LSRgBody01
                          style={{
                            color: "#101113",
                            width: 169,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item?.title || ""}
                        </LSRgBody01>
                      </td>
                      <td>
                        <LSRgCaption01 style={{ color: "#898E99" }}>
                          {moment(item.createdAt).format("YYYY.MM.DD")}
                        </LSRgCaption01>
                      </td>
                    </Tr>
                    {item.isOpen && (
                      <tr
                        style={{
                          width: 335,
                          minHeight: 63,
                          height: "fit-content",
                          borderBottom: "1px solid #dbdde0",
                          padding: "10px",
                          display: "flex",
                          alignItems: "center",
                          margin: "10px 0px",
                        }}
                      >
                        <td>
                          <LSRgBody01
                            style={{
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                            }}
                          >
                            {item?.content || ""}
                          </LSRgBody01>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </Table>

            {noticeList.length === 0 && (
              <Empty title={"등록된 공지사항이 없습니다."} />
            )}

            {noticeList.length > 0 && pagination.totalCount > 5 && (
              <Pagination
                route={route.customer_service_notice}
                queryString={``}
                currentPage={Number(page) || 1}
                totalPages={pagination.totalPages}
                rangeSize={pagination.rangeSize}
              />
            )}
          </ContentContainer>
        </MOContainer>
      )}
    </HomeSection>
  );
};

export default CustomerServiceNotice;

const HomeSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: ${(props) =>
    props.$isSmall ? "40px 0px 80px 0px" : "80px 0px 150px 0px"};
`;

const TabContainer = styled.div.attrs((props) => {})`
  width: 175px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ContentContainer = styled.div.attrs((props) => {})`
  width: ${(props) => (props.$isSmall ? "335px" : "995px")};
  height: fit-content;
  display: flex;
  flex-direction: column;
`;

const ContentHeaderWrapper = styled.div.attrs((props) => {})`
  display: flex;
  flex-direction: column;
  gap: ${(props) => (props.$isSmall ? "16px" : "4px")};
  margin-bottom: ${(props) => (props.$isSmall ? "40px" : "30px")};
`;

const Table = styled.table.attrs((props) => {})`
  width: ${(props) => (props.$isSmall ? "335px" : "995px")};
  border-spacing: 0px;
  border-collapse: collapse;
`;

const Tr = styled.tr.attrs((props) => {})`
  width: ${(props) => (props.$isSmall ? "335px" : "995px")};
  height: 63px;
  border-bottom: 1px solid #dbdde0;
  padding: 16px 10px;
  display: flex;
  align-items: center;
`;
